import React, { FunctionComponent, useContext } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { Box, SidePanel, TextButton, Text, Button } from '@wix/design-system';
import DismissSmall from 'wix-ui-icons-common/DismissSmall';
import { isMobile, isTablet } from '../../utils/userAgent';
import { Context } from '../../context';
import ReactDOM from 'react-dom';
import { useBI } from '../../hooks/useBI';
import { ALL_PRODUCTS_LABEL_ID, BI } from '../../constants';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { useRouter } from 'next/router';
import { DATA_HOOKS } from '../../dataHooks';
import { animateScroll } from 'react-scroll';
import { helpCenterPageClick } from '@wix/bi-logger-customer-care-data/v2';
const RoadmapFeatureRequest = dynamic(() => import('../RoadmapFeatureRequest'));
import css from './index.module.scss';

const HIDDEN_SIDE_PANEL_OFFSET = -440;

type RoadmapLabelsListProps = {
  labels: { id: string; name: string }[];
  selectedLabel: { id: string; name: string };
  onSelectLabel: (label: { id: string; name: string }) => void;
};

export const RoadmapLabelsList: FunctionComponent<RoadmapLabelsListProps> = ({
  labels,
  selectedLabel,
  onSelectLabel,
}: RoadmapLabelsListProps) => {
  const { t } = useTranslation();
  const [left, setLeft] = React.useState(HIDDEN_SIDE_PANEL_OFFSET);
  const context = useContext(Context);
  const { sendBIEvent } = useBI();
  const isMobileView = isMobile(context);
  const isTabletView = isTablet(context);
  const { isWixStudioUser } = useContext(Context);
  const { locale } = useRouter();
  const [showFeatureRequestModal, setShowFeatureRequestModal] =
    React.useState(false);

  const openPanel = async () => {
    setLeft(0);
    document.body.classList.add('side-panel-open');
    animateScroll.scrollToTop();
    await sendBIEvent(
      helpCenterPageClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: 'open_category_label_list',
      })
    );
  };

  const closePanel = async () => {
    setLeft(HIDDEN_SIDE_PANEL_OFFSET);
    document.body.classList.remove('side-panel-open');
    await sendBIEvent(
      helpCenterPageClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: 'close_category_label_list',
      })
    );
  };
  const RoadmapLabel = (label: { id: string; name: string }) => {
    const onClickLabel = async () => {
      onSelectLabel(label);
      if (isMobileView || isTabletView) {
        return closePanel();
      }
    };
    const isSelected = label.id === selectedLabel.id;
    return (
      <Box key={label.id} className={css.labelNameWrapper}>
        <TextButton
          className={isSelected ? css.labelNameSelected : css.labelName}
          onClick={onClickLabel}
          skin={isSelected ? 'standard' : 'dark'}
        >
          {label.name}
        </TextButton>
      </Box>
    );
  };

  const onClickFeatureRequest = async () => {
    setShowFeatureRequestModal(true);
    setLeft(HIDDEN_SIDE_PANEL_OFFSET);
    await sendBIEvent(
      helpCenterPageClick({
        source_name: BI.SOURCE_NAMES.ROADMAP,
        clicked_item_type: 'request_a_feature',
        clicked_text: 'request_a_feature',
        kb_lang: locale as string,
        item_id:
          selectedLabel && selectedLabel.id !== ALL_PRODUCTS_LABEL_ID
            ? selectedLabel.id
            : undefined,
      })
    );
  };

  const RenderLabelList = (
    <Box direction="vertical" className={`${css.labelList}`}>
      {labels.map(RoadmapLabel)}
    </Box>
  );

  const isMobileLandscape = isMobileView && isTabletView;
  const specifClassName = isMobileLandscape ? css.mobileLandscape : '';

  const renderLabelsPanel = () => (
    <div className={`${css.mobileViewLabelListPanel} ${specifClassName}`}>
      <SidePanel
        className={css.LabelListPanel}
        onCloseButtonClick={closePanel}
        width="265px"
      >
        <SidePanel.Content className={css.labelsSidePanelContent}>
          <Box
            direction="horizontal"
            verticalAlign="middle"
            align="space-between"
            paddingBottom="12px"
          >
            <Box align="center" direction="horizontal" verticalAlign="middle">
              <div className={css.labelsListIconWrapper}>
                <div
                  className={css.labelsListIcon}
                  onClick={() => closePanel()}
                >
                  <Image
                    src={`https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/menu.svg`}
                    width={16}
                    height={12}
                    alt=""
                    loading="eager"
                  />
                </div>
              </div>
              <Text size="small" className={css.selectedLabelHeader}>
                {selectedLabel.name}
              </Text>
            </Box>
            <div
              className={css.dismissSmallButton}
              onClick={() => closePanel()}
            >
              <DismissSmall />
            </div>
          </Box>
          {isWixStudioUser && (
            <>
              <Button
                className={css.requestFeatureButton}
                onClick={onClickFeatureRequest}
                priority="secondary"
                skin="dark"
                size={isMobileView ? 'small' : 'medium'}
                dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_BUTTON}
              >
                {t('roadmap.feature-request')}
              </Button>
            </>
          )}
          {RenderLabelList}
        </SidePanel.Content>
      </SidePanel>
    </div>
  );
  const mobileLabelsMenu = () => (
    <div
      className={css.mobileViewLabelList}
      data-hook={DATA_HOOKS.LABELS_LIST_ICON}
    >
      <div className={css.labelsListIcon} onClick={() => openPanel()}>
        <Image
          src={`https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/menu.svg`}
          width={16}
          height={12}
          alt=""
          loading="eager"
        />
      </div>
      {left === 0 && ReactDOM.createPortal(renderLabelsPanel(), document.body)}
    </div>
  );

  return isMobileView ? (
    <>
      {mobileLabelsMenu()}
      <RoadmapFeatureRequest
        labels={labels}
        isOpen={showFeatureRequestModal}
        onClose={() => setShowFeatureRequestModal(false)}
      />
    </>
  ) : (
    <>
      {mobileLabelsMenu()}
      <div className={`${css.desktopLabelList}`}>{RenderLabelList}</div>
    </>
  );
};
